import { useSaveExportConfig } from "@/api/export";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { extractLabel } from "@/config/forms/advanced-search/advanced-search-fields-def";
import { toast } from "@/hooks/use-toast";
import { ExportConfig } from "@/lib/auth/export/export-types";
import { TabMenuItems } from "@/lib/types";
import { Download, Loader2 } from "lucide-react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SaveExport } from "./ExportModals";

const ExportContent = () => {
  const { t } = useTranslation();
  const { control } = useFormContext<ExportConfig>();
  const watchedFields = useWatch({ control }) as ExportConfig;

  const numberOfSelectedFields = watchedFields
    ? Object.values(watchedFields).reduce((acc, field) => {
        if (field.type !== "group") {
          return acc + (field.checked ? 1 : 0);
        }
        return (
          acc +
          field.fields.reduce((acc, field) => acc + (field.checked ? 1 : 0), 0)
        );
      }, 0)
    : 0;

  return (
    <>
      <div className="flex justify-between">
        <p className="font-semibold">{t("export.summary")}</p>
        <p className="text-sm text-gray-700">
          {numberOfSelectedFields + " " + t("export.selectedFields")}
        </p>
      </div>
      <div className="flex flex-col p-5 gap-8 bg-gray-50 rounded-xl h-[50vh] overflow-y-auto">
        {watchedFields &&
          Object.values(watchedFields).map((field) => {
            if (field.type !== "group") {
              if (!field.checked) return null;
              return (
                <div
                  key={field.key + field.label}
                  className="flex flex-col gap-3"
                >
                  <Separator />
                  <p className="text-sm font-medium text-gray-700">
                    {t(extractLabel(field))}
                  </p>
                </div>
              );
            }
            if (field.fields.every((field) => !field.checked)) return null;
            return (
              <div key={field.label} className="flex flex-col gap-3">
                <p className="font-semibold text-gray-700">{t(field.label)}</p>
                <Separator />
                {field.fields.map((field) => {
                  if (!field.checked) return null;
                  return (
                    <p
                      key={field.key + field.label}
                      className="text-sm font-medium text-gray-700"
                    >
                      {t(extractLabel(field))}
                    </p>
                  );
                })}
              </div>
            );
          })}
      </div>
    </>
  );
};

type ExportSummaryProps = {
  selectedTable: TabMenuItems;
  onSubmit: () => void;
  numberOfResults: number;
  isPending: boolean;
};

export const ExportSummary = ({
  selectedTable,
  onSubmit,
  numberOfResults,
  isPending,
}: ExportSummaryProps) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext<ExportConfig>();
  const { mutateAsync: saveExport } = useSaveExportConfig();
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const { control } = useFormContext<ExportConfig>();
  const watchedFields = useWatch({ control }) as ExportConfig;

  const numberOfSelectedFields = watchedFields
    ? Object.values(watchedFields).reduce((acc, field) => {
        if (field.type !== "group") {
          return acc + (field.checked ? 1 : 0);
        }
        return (
          acc +
          field.fields.reduce((acc, field) => acc + (field.checked ? 1 : 0), 0)
        );
      }, 0)
    : 0;

  const handleSave = async (exportName: string) => {
    const values = getValues();
    const name = exportName.length > 0 ? exportName : t("export.defaultName");
    await saveExport(
      {
        name,
        table: selectedTable,
        config: values,
      },
      {
        onSuccess: () => {
          toast({
            variant: "success",
            title: t("export.save.success"),
          });
          setSaveModalOpen(false);
        },
        onError: () => {
          toast({
            variant: "destructive",
            title: t("export.save.error"),
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 p-6 h-fit rounded-xl border bg-white border-gray-200 w-[30%]">
      <ExportContent />
      <div className="flex gap-2">
        <SaveExport
          handleSave={handleSave}
          open={saveModalOpen}
          setOpen={setSaveModalOpen}
        />
        <Button
          className="flex-1 gap-1"
          onClick={onSubmit}
          disabled={
            isPending || numberOfSelectedFields === 0 || numberOfResults === 0
          }
        >
          <span className="text-sm font-medium">
            {isPending ? t("export.exporting") : t("export")}
          </span>
          {isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Download className="w-4 h-4" />
          )}
        </Button>
      </div>
    </div>
  );
};
